import { type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import type { ReleaseRow } from '../lib/box-lifecycle';
import { canDoKey, upToDate } from '../lib/fleet-machine-panel';
import type { UtilBox } from '../lib/fleetUtilization';
import { serverText } from '../lib/machine-labels';
import { day, decimal } from '../lib/time';
import { MachineJobsAtOnce } from './MachineJobsAtOnce';
import { MachineServerName } from './MachineServerName';

// "About this machine" (#6563, prototype `panel()` `dl.about`): its size, its software and
// whether that is up to date, what it can do, who it works for, when it connected, the
// server and its full id. Every row is one the row or the point read actually carries;
// a value nobody reported reads "-", never a guess. The shared row-shape `.facts--rows`
// (`styles/ui/_facts.scss`), the prototype's two-column `dl.about`.
//
// THE SERVER ROW CARRIES ITS EDIT (#6566, `MachineServerName`): an admin on this org's own
// machine names the whole server from here.
//
// "JOBS AT ONCE" CLOSES THE SECTION (#6740, `MachineJobsAtOnce`): the stored cap, a `Select`
// for an admin on this org's own machine, plain text for everyone else (G21, G23).
//
// "UP TO DATE" is `upToDate` (`lib/fleet-machine-panel.ts`): the newest matured stable
// release for the machine's arch. The arch comes off the point read (`specs.arch`); with
// no specs there is no badge.

const GB = 1e9;
const NONE = 'fleet.machines.not_reported';

export function MachineAbout(props: {
  readonly box: UtilBox;
  /** `specs.arch` off `GET /v1/runners/:id`, or null (no specs, or not read). */
  readonly arch: string | null;
  readonly releases: readonly ReleaseRow[];
  /** The UNFILTERED roster, for the Server edit's write (every own-org machine on the server). */
  readonly allBoxes: readonly UtilBox[];
  /** `canAdminWrite` on a settled role (owner ruling G21). */
  readonly canWrite: boolean;
  readonly token: string | null;
  readonly notify: (message: string, tone?: 'success' | 'error') => void;
  readonly onWritten: () => void;
}): JSX.Element {
  const gb = (bytes: number | null | undefined): string =>
    bytes === null || bytes === undefined
      ? t(NONE)
      : t('fleet.panel.gb', { value: decimal(bytes / GB) });
  const current = (): boolean =>
    upToDate({
      version: props.box.runner_version,
      arch: props.arch,
      releases: props.releases,
      nowMs: Date.now(),
    });
  const canDo = () => canDoKey(props.box.mode);
  return (
    <section class="machine-panel__section" aria-labelledby="machine-panel-about">
      <h3 id="machine-panel-about" class="machine-panel__h3">
        {t('fleet.panel.about')}
      </h3>
      <dl class="facts facts--rows">
        <div>
          <dt>{t('fleet.panel.about_processors')}</dt>
          <dd class="facts__num">{props.box.specs?.cpu_cores ?? t(NONE)}</dd>
        </div>
        <div>
          <dt>{t('fleet.panel.about_memory')}</dt>
          <dd class="facts__num">{gb(props.box.specs?.mem_bytes)}</dd>
        </div>
        <div>
          <dt>{t('fleet.panel.about_disk')}</dt>
          <dd class="facts__num">{gb(props.box.stats?.workspaceDisk?.totalBytes)}</dd>
        </div>
        <div>
          <dt>{t('fleet.panel.about_version')}</dt>
          <dd>
            <span class="facts__num">{props.box.runner_version ?? t(NONE)}</span>
            <Show when={current()}>
              <span class="machine-panel__muted"> · {t('fleet.panel.up_to_date')}</span>
            </Show>
          </dd>
        </div>
        <Show when={canDo()}>
          {(key) => (
            <div>
              <dt>{t('fleet.panel.about_can_do')}</dt>
              <dd>{t(key())}</dd>
            </div>
          )}
        </Show>
        <Show when={props.box.owner?.label}>
          {(label) => (
            <div>
              <dt>{t('fleet.panel.about_works_for')}</dt>
              <dd class="facts__num">{label()}</dd>
            </div>
          )}
        </Show>
        <Show when={props.box.enrolled_iso}>
          {(iso) => (
            <div>
              <dt>{t('fleet.panel.about_connected')}</dt>
              <dd>{day(iso())}</dd>
            </div>
          )}
        </Show>
        <Show when={serverText(props.box)}>
          {(server) => (
            <div>
              <dt>{t('fleet.panel.about_server')}</dt>
              <dd>
                <MachineServerName
                  box={props.box}
                  text={server()}
                  allBoxes={props.allBoxes}
                  canWrite={props.canWrite}
                  token={props.token}
                  notify={props.notify}
                  onWritten={props.onWritten}
                />
              </dd>
            </div>
          )}
        </Show>
        <div>
          <dt>{t('fleet.panel.about_id')}</dt>
          <dd class="facts__num">{props.box.id}</dd>
        </div>
      </dl>
      <MachineJobsAtOnce
        box={props.box}
        canWrite={props.canWrite}
        variant="row"
        token={props.token}
      />
    </section>
  );
}
